import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import {
  getColor,
  getBaseUnit,
  getFontSize,
  getFontWeight,
} from '../styles/themeGetters';
import { ButtonLoadingIndicator } from './ButtonLoadingIndicator';
import { IconComponent } from '../Icons/helpers';
import { ColorKeys } from '../styles/themes';

type Color = 'primary' | 'danger' | 'ai';

type StyledButtonProps = {
  color: Color;
  variant: 'contained' | 'outlined' | 'text';
  size: 's' | 'l';
  $loading?: boolean;
  iconOnly?: boolean;
  withStartIcon: boolean;
  withEndIcon: boolean;
};

const getColorOptions =
  (options: Record<Color, string>) =>
  ({ color }) =>
    options[color];

const variantContained = css<StyledButtonProps>`
  color: ${getColorOptions({
    primary: getColor('white'),
    danger: getColor('white'),
    ai: getColor('lilac-dark'),
  })};
  background-color: ${getColorOptions({
    primary: getColor('navy'),
    danger: getColor('red'),
    ai: getColor('lilac-light'),
  })};
  border-color: ${getColorOptions({
    primary: 'transparent',
    danger: 'transparent',
    ai: getColor('lilac'),
  })};

  &:hover:enabled {
    background-color: ${getColorOptions({
      primary: getColor('navy-dark'),
      danger: getColor('red-dark'),
      ai: getColor('lilac-light'),
    })};
    border-color: ${getColorOptions({
      primary: 'transparent',
      danger: 'transparent',
      ai: getColor('lilac-dark'),
    })};
  }

  &:focus {
    background-color: ${getColorOptions({
      primary: getColor('navy'),
      danger: getColor('red'),
      ai: getColor('lilac-light'),
    })};
    border-color: ${getColorOptions({
      primary: getColor('blue-light'),
      danger: getColor('red-light'),
      ai: getColor('lilac-dark'),
    })};
  }

  &:active:enabled {
    background-color: ${getColorOptions({
      primary: getColor('navy-dark'),
      danger: getColor('red-dark'),
      ai: getColor('lilac-light'),
    })};
    border-color: ${getColorOptions({
      primary: 'transparent',
      danger: 'transparent',
      ai: getColor('lilac-dark'),
    })};
  }

  &:disabled {
    color: ${getColorOptions({
      primary: getColor('grey-dark'),
      danger: getColor('red-light'),
      ai: getColor('lilac'),
    })};
    border-color: ${getColorOptions({
      primary: 'transparent',
      danger: 'transparent',
      ai: getColor('lilac-light'),
    })};
  }
`;

const variantOutlined = css<StyledButtonProps>`
  color: ${getColorOptions({
    primary: getColor('navy'),
    danger: getColor('red'),
    ai: getColor('lilac-dark'),
  })};
  border: 1px solid
    ${getColorOptions({
      primary: getColor('grey-light-100'),
      danger: getColor('red'),
      ai: getColor('lilac-dark'),
    })};
  background-color: ${getColor('white')};

  &:hover:enabled {
    background-color: ${getColorOptions({
      primary: getColor('grey-light-100'),
      danger: getColor('red-light'),
      ai: getColor('lilac-light'),
    })};
  }

  &:focus {
    border: 1px solid
      ${getColorOptions({
        primary: getColor('blue-light'),
        danger: getColor('red-light'),
        ai: getColor('lilac-light'),
      })};
  }

  &:active:enabled {
    border: 1px solid
      ${getColorOptions({
        primary: getColor('grey-light-100'),
        danger: getColor('red'),
        ai: getColor('lilac-dark'),
      })};
    background-color: ${getColorOptions({
      primary: getColor('grey-light-50'),
      danger: getColor('red-light'),
      ai: getColor('lilac-light'),
    })};
  }

  &:disabled {
    color: ${getColorOptions({
      primary: getColor('grey-dark'),
      danger: getColor('red-light'),
      ai: getColor('lilac-light'),
    })};
    border: 1px solid
      ${getColorOptions({
        primary: getColor('grey-light-100'),
        danger: getColor('red-light'),
        ai: getColor('lilac-light'),
      })};

    border-color: ${({ $loading }) =>
      $loading &&
      getColorOptions({
        primary: getColor('grey-light-100'),
        danger: getColor('red'),
        ai: getColor('lilac-dark'),
      })};
  }
`;

const variantText = css<StyledButtonProps>`
  color: ${getColorOptions({
    primary: getColor('navy'),
    danger: getColor('red'),
    ai: getColor('lilac-dark'),
  })};

  &:hover:enabled {
    background-color: ${getColorOptions({
      primary: getColor('grey-light-100'),
      danger: getColor('red-light'),
      ai: getColor('lilac-light'),
    })};
  }

  &:focus {
    border: 1px solid
      ${getColorOptions({
        primary: getColor('blue-light'),
        danger: getColor('red-light'),
        ai: getColor('lilac-light'),
      })};
  }

  &:active:enabled {
    border: 1px solid
      ${getColorOptions({
        primary: getColor('grey-light-100'),
        danger: getColor('red-light'),
        ai: getColor('lilac-light'),
      })};
    background-color: ${getColorOptions({
      primary: getColor('grey-light-50'),
      danger: getColor('red-light'),
      ai: getColor('lilac-light'),
    })};
  }

  &:disabled {
    color: ${getColorOptions({
      primary: getColor('grey-dark'),
      danger: getColor('red-light'),
      ai: getColor('lilac-light'),
    })};

    /* SVG fill color when button is disabled */
    svg {
      fill: ${getColorOptions({
        primary: getColor('grey-dark'),
        danger: getColor('red-light'),
        ai: getColor('lilac-light'),
      })};
    }
  }

  /* SVG fill color for outlined variant */
  svg {
    fill: ${getColorOptions({
      primary: getColor('navy'),
      danger: getColor('red'),
      ai: getColor('lilac-dark'),
    })};
  }
`;

const variants = {
  contained: variantContained,
  outlined: variantOutlined,
  text: variantText,
};

const sizeLargeStyle = css`
  height: ${getBaseUnit(9)};
`;

const sizeSmallStyle = css`
  height: ${getBaseUnit(7)};
`;

const sizeStyles = {
  l: sizeLargeStyle,
  s: sizeSmallStyle,
};

const StyledButton = styled.button<StyledButtonProps>`
  all: unset;

  /* Generic styles for the button */

  font-size: ${getFontSize(14)};
  font-weight: ${getFontWeight('semibold')};

  padding-left: ${({ iconOnly, withStartIcon, size }) => {
    if (iconOnly) return size === 'l' ? getBaseUnit(1.75) : getBaseUnit(0.75);
    if (withStartIcon) return size === 'l' ? getBaseUnit(2) : getBaseUnit(1.5);
    return size === 'l' ? getBaseUnit(4) : getBaseUnit(2);
  }};
  padding-right: ${({ iconOnly, withEndIcon, size }) => {
    if (iconOnly) return size === 'l' ? getBaseUnit(1.75) : getBaseUnit(0.75);
    if (withEndIcon) return size === 'l' ? getBaseUnit(2) : getBaseUnit(1.5);
    return size === 'l' ? getBaseUnit(4) : getBaseUnit(2);
  }};

  box-sizing: border-box;
  border-radius: ${getBaseUnit(1)};

  &:hover:enabled {
    cursor: pointer;
  }

  border: 1px solid transparent;

  ${props => variants[props.variant]};
  ${props => sizeStyles[props.size]};

  position: relative;

  /* SVG Icons size */
  svg {
    font-size: ${getFontSize(24)};
  }
`;

const ContentWrapper = styled.div<{ $loading?: boolean }>`
  opacity: ${({ $loading }) => ($loading ? 0 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${getBaseUnit(1.5)};
`;

const OpticallyAlignedTextContainer = styled.div`
  padding-bottom: ${getBaseUnit(0.25)};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type ButtonProps = {
  color?: Color;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 's' | 'l';
  loading?: boolean;
  startIcon?: IconComponent;
  endIcon?: IconComponent;
  disabled?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const getIconColor: (props: ButtonProps) => ColorKeys = ({
  variant,
  disabled,
  color,
}) => {
  if (disabled) {
    return getColorOptions({
      primary: 'grey-dark',
      danger: 'red-light',
      ai: 'lilac-light',
    })({ color });
  }
  switch (variant) {
    case 'outlined':
    case 'text':
      return getColorOptions({
        primary: 'navy',
        danger: 'red',
        ai: 'lilac-dark',
      })({ color });
    case 'contained':
      return getColorOptions({
        primary: 'white',
        danger: 'white',
        ai: 'lilac-dark',
      })({ color });
  }
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      color = 'primary',
      variant = 'contained',
      size = 'l',
      loading,
      startIcon: StartIcon,
      endIcon: EndIcon,
      disabled,
      ...rest
    },
    ref
  ) => {
    const iconColor = getIconColor({ color, variant, disabled });

    return (
      <StyledButton
        {...rest}
        color={color}
        variant={variant}
        size={size}
        disabled={disabled || loading}
        $loading={loading}
        aria-busy={loading}
        iconOnly={!children}
        withStartIcon={!!StartIcon}
        withEndIcon={!!EndIcon}
        ref={ref}
      >
        <ContentWrapper $loading={loading}>
          {StartIcon && <StartIcon color={iconColor} />}

          {children && (
            <OpticallyAlignedTextContainer>
              {children}
            </OpticallyAlignedTextContainer>
          )}

          {EndIcon && <EndIcon color={iconColor} />}
        </ContentWrapper>

        {loading && (
          <ButtonLoadingIndicator color={color} variant={variant} size={size} />
        )}
      </StyledButton>
    );
  }
);

export { Button };
